/**
 * Optiunile unei oferte, din perspectiva interfetei.
 *
 * Formele nu se mai declara aici: `OfferOptionGroupDto` / `OfferOptionDto` vin
 * din `@deliveryapp/shared`, deci contractul e unul singur pentru core, POS si
 * web. Ce ramane in fisierul asta e strict logica de ecran, cea care nu are ce
 * cauta in contract: ce blocheaza adaugarea in cos, cat costa alegerea facuta si
 * ce identitate are linia de cos care rezulta.
 *
 * Un singur lucru pare sa dubleze contractul si nu il dubleaza: verificarea la
 * RULARE a grupurilor. Tipul spune ca `optionGroups` e o lista de grupuri, dar
 * tipul e o promisiune de compilare, iar valoarea vine peste retea, de la un
 * core care poate fi mai nou sau mai vechi (ADR-002). De aceea regula ramane
 * scrisa in cod, nu doar in comentariu:
 *
 *  - grupuri ABSENTE sau lista goala = "produsul nu are optiuni". Stare normala;
 *  - grupuri PREZENTE dar necitibile = produs NEadaugabil, cu mesaj. Aici nu se
 *    poate sti daca exista un grup obligatoriu, iar tratarea lor ca "fara
 *    optiuni" ar trimite la bucatarie o pizza fara blat ales — exact defectul pe
 *    care ADR-024 il inchide. Fail-closed, ca in ADR-012;
 *  - un `priceDelta` prezent dar necitibil pica grupul intreg. Devenit tacit 0,
 *    ar aparea pe bon un supliment gratuit.
 */

import type {
  MoneyMinor,
  NutritionDto,
  OfferDto,
  OfferFulfillmentVariantDto,
  OfferOptionDto,
  OfferOptionGroupDto,
} from "@deliveryapp/shared";

export interface OfferOptions {
  groups: OfferOptionGroupDto[];
  variants: OfferFulfillmentVariantDto[];
  /**
   * `true` cand oferta declara optiuni pe care aplicatia nu le poate citi.
   * Consumatorul NU are voie sa adauge produsul in cos in starea asta.
   */
  unreadable: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readMoney(value: unknown): MoneyMinor | null {
  return typeof value === "number" && Number.isSafeInteger(value) ? value : null;
}

const NUTRITION_FIELDS = [
  "basisGrams", "energyKcal", "energyKj", "fat", "saturatedFat",
  "carbohydrates", "sugars", "protein", "salt", "fibre",
] as const;

function readNutrition(raw: unknown): NutritionDto | null {
  if (!isRecord(raw)) return null;
  const nutrition: NutritionDto = {};
  let hasValue = false;
  for (const field of NUTRITION_FIELDS) {
    const value = raw[field];
    if (typeof value !== "number" || !Number.isFinite(value)) continue;
    nutrition[field] = value;
    hasValue = true;
  }
  return hasValue ? nutrition : null;
}

function readOption(raw: unknown): OfferOptionDto | null {
  if (!isRecord(raw)) return null;
  const id = raw.id;
  const name = raw.name;
  if (typeof id !== "string" || id.length === 0) return null;
  if (typeof name !== "string" || name.length === 0) return null;
  // `priceDelta` lipsa inseamna "fara diferenta de pret" doar daca lipseste de
  // tot. O valoare prezenta pe care nu o putem citi ca suma ar deveni tacit 0.
  const priceDelta = raw.priceDelta === undefined || raw.priceDelta === null ? 0 : readMoney(raw.priceDelta);
  if (priceDelta === null) return null;
  const available = raw.available === undefined ? true : raw.available === true;
  const allergens = Array.isArray(raw.allergens)
    ? raw.allergens.filter((entry): entry is string => typeof entry === "string" && entry.trim().length > 0)
    : [];
  const dietTags = Array.isArray(raw.dietTags)
    ? raw.dietTags.filter((entry): entry is string => typeof entry === "string" && entry.trim().length > 0)
    : [];
  const foodImpact = raw.foodImpact === "linked_product" || raw.foodImpact === "no_impact"
    ? raw.foodImpact
    : undefined;
  const nutrition = readNutrition(raw.nutrition);
  const linkedVatRate =
    raw.linkedVatRate === null || raw.linkedVatRate === undefined
      ? null
      : typeof raw.linkedVatRate === "number" && [0, 11, 21].includes(raw.linkedVatRate)
        ? raw.linkedVatRate
        : null;
  return { id, name, priceDelta, available, allergens, dietTags, nutrition, foodImpact, linkedVatRate };
}

function readGroup(raw: unknown): OfferOptionGroupDto | null {
  if (!isRecord(raw)) return null;
  const id = raw.id;
  const name = raw.name;
  if (typeof id !== "string" || id.length === 0) return null;
  if (typeof name !== "string" || name.length === 0) return null;
  if (!Array.isArray(raw.options)) return null;

  const options: OfferOptionDto[] = [];
  for (const entry of raw.options) {
    const option = readOption(entry);
    if (option === null) return null;
    options.push(option);
  }

  const required = raw.required === true;
  const minSelectRaw = raw.minSelect;
  const maxSelectRaw = raw.maxSelect;
  const minSelect =
    typeof minSelectRaw === "number" && Number.isSafeInteger(minSelectRaw) && minSelectRaw >= 0
      ? minSelectRaw
      : required
        ? 1
        : 0;
  const maxSelect =
    typeof maxSelectRaw === "number" && Number.isSafeInteger(maxSelectRaw) && maxSelectRaw >= 1
      ? maxSelectRaw
      : 1;
  if (maxSelect < minSelect) return null;
  return { id, name, minSelect, maxSelect, required: required || minSelect > 0, options };
}

export function readOfferOptions(offer: OfferDto): OfferOptions {
  // Citirea se face pe valoarea bruta: tipul garanteaza forma la compilare, nu
  // si ce a trimis serverul. Fara pasul asta, un `optionGroups` malformat ar
  // trece drept lista de grupuri si ar produce alegeri inexistente.
  const raw = (offer as unknown as Record<string, unknown>).optionGroups;

  const rawVariants = (offer as unknown as Record<string, unknown>).fulfillmentVariants;
  const variants: OfferFulfillmentVariantDto[] = [];
  if (Array.isArray(rawVariants)) {
    for (const rawVariant of rawVariants) {
      if (!isRecord(rawVariant) || !Array.isArray(rawVariant.rules) || !Array.isArray(rawVariant.availableOptionIds)) {
        return { groups: [], variants: [], unreadable: true };
      }
      const rules: OfferFulfillmentVariantDto["rules"] = [];
      for (const rawRule of rawVariant.rules) {
        if (!isRecord(rawRule)) return { groups: [], variants: [], unreadable: true };
        const { groupId, minSelect, maxSelect } = rawRule;
        if (
          typeof groupId !== "string" ||
          !Number.isSafeInteger(minSelect) ||
          !Number.isSafeInteger(maxSelect) ||
          (minSelect as number) < 0 ||
          (maxSelect as number) < 1 ||
          (maxSelect as number) < (minSelect as number)
        ) return { groups: [], variants: [], unreadable: true };
        rules.push({
          groupId,
          minSelect: minSelect as number,
          maxSelect: maxSelect as number,
          required: rawRule.required === true,
        });
      }
      if (!rawVariant.availableOptionIds.every((id) => typeof id === "string")) {
        return { groups: [], variants: [], unreadable: true };
      }
      variants.push({ rules, availableOptionIds: rawVariant.availableOptionIds as string[] });
    }
  }

  if (raw === undefined || raw === null) return { groups: [], variants, unreadable: false };
  if (!Array.isArray(raw)) return { groups: [], variants: [], unreadable: true };

  const groups: OfferOptionGroupDto[] = [];
  for (const entry of raw) {
    const group = readGroup(entry);
    if (group === null) return { groups: [], variants: [], unreadable: true };
    groups.push(group);
  }
  return { groups, variants, unreadable: false };
}

// ---------------------------------------------------------------------------
// Selectia clientului
// ---------------------------------------------------------------------------

/** `groupId -> optionId[]`. Ordinea din grup e ordinea in care se tipareste. */
export type OptionSelection = Record<string, string[]>;

export function selectedIdsFor(selection: OptionSelection, groupId: string): string[] {
  return selection[groupId] ?? [];
}

/**
 * Ce mai lipseste ca produsul sa poata fi adaugat. Lista goala = se poate.
 * Textele sunt cele afisate langa grup, deci spun ce e de facut, nu ce e gresit.
 */
export function selectionBlockers(groups: OfferOptionGroupDto[], selection: OptionSelection): string[] {
  const blockers: string[] = [];
  for (const group of groups) {
    const chosen = selectedIdsFor(selection, group.id).filter((optionId) =>
      group.options.some((option) => option.id === optionId && option.available),
    );
    if (chosen.length < group.minSelect) {
      const missing = group.minSelect - chosen.length;
      blockers.push(
        group.minSelect === 1
          ? `Alege o opțiune la «${group.name}».`
          : `Mai alege ${missing} ${missing === 1 ? "opțiune" : "opțiuni"} la «${group.name}».`,
      );
    }
    if (chosen.length > group.maxSelect) {
      blockers.push(`La «${group.name}» poți alege cel mult ${group.maxSelect}.`);
    }
  }
  return blockers;
}

function variantSupportsSelection(
  variant: OfferFulfillmentVariantDto,
  selection: OptionSelection,
  complete: boolean,
): boolean {
  const available = new Set(variant.availableOptionIds);
  const rules = new Map(variant.rules.map((rule) => [rule.groupId, rule]));
  for (const [groupId, optionIds] of Object.entries(selection)) {
    if (optionIds.length === 0) continue;
    const rule = rules.get(groupId);
    if (rule === undefined || optionIds.some((optionId) => !available.has(optionId))) return false;
    if (optionIds.length > rule.maxSelect) return false;
  }
  if (!complete) return true;
  return variant.rules.every((rule) => {
    const count = selectedIdsFor(selection, rule.groupId).length;
    const minimum = rule.required ? Math.max(1, rule.minSelect) : rule.minSelect;
    return count >= minimum && count <= rule.maxSelect;
  });
}

/** Cel puțin o bucătărie eligibilă poate executa întreaga configurație. */
export function selectionFitsFulfillmentVariant(
  variants: readonly OfferFulfillmentVariantDto[],
  selection: OptionSelection,
  complete = true,
): boolean {
  return variants.length === 0 || variants.some((variant) => variantSupportsSelection(variant, selection, complete));
}

/**
 * După fiecare alegere păstrăm numai bucătăriile care mai pot executa selecția
 * și proiectăm regulile lor. Astfel un grup care era opțional în uniunea inițială
 * devine imediat obligatoriu dacă alegerile anterioare au rămas compatibile doar
 * cu restaurantul care îl cere.
 */
export function projectOptionGroupsForSelection(
  groups: readonly OfferOptionGroupDto[],
  variants: readonly OfferFulfillmentVariantDto[],
  selection: OptionSelection,
): OfferOptionGroupDto[] {
  if (variants.length === 0) return [...groups];
  const compatible = variants.filter((variant) => variantSupportsSelection(variant, selection, false));
  if (compatible.length === 0) return [...groups];
  return groups.flatMap((group) => {
    const rules = compatible.map((variant) => variant.rules.find((rule) => rule.groupId === group.id));
    const declared = rules.filter((rule): rule is NonNullable<typeof rule> => rule !== undefined);
    if (declared.length === 0) return [];
    const omitted = rules.some((rule) => rule === undefined);
    const minSelect = omitted
      ? 0
      : Math.min(...declared.map((rule) => (rule.required ? Math.max(1, rule.minSelect) : rule.minSelect)));
    const maxSelect = Math.max(...declared.map((rule) => rule.maxSelect));
    return [{ ...group, minSelect, maxSelect, required: minSelect > 0 }];
  });
}

/** O alegere rămâne activă numai dacă duce spre cel puțin o configurație rutabilă. */
export function optionKeepsFulfillmentPossible(
  variants: readonly OfferFulfillmentVariantDto[],
  selection: OptionSelection,
  groupId: string,
  optionId: string,
  maxSelect: number,
): boolean {
  if (variants.length === 0) return true;
  const chosen = selectedIdsFor(selection, groupId);
  const next = chosen.includes(optionId)
    ? chosen.filter((id) => id !== optionId)
    : maxSelect <= 1
      ? [optionId]
      : [...chosen, optionId];
  return selectionFitsFulfillmentVariant(variants, { ...selection, [groupId]: next }, false);
}

export function selectionAfterOptionToggle(
  selection: OptionSelection,
  groupId: string,
  optionId: string,
  maxSelect: number,
): OptionSelection {
  const chosen = selectedIdsFor(selection, groupId);
  const next = chosen.includes(optionId)
    ? chosen.filter((id) => id !== optionId)
    : maxSelect <= 1
      ? [optionId]
      : [...chosen, optionId];
  return { ...selection, [groupId]: next };
}

/**
 * Există cel puțin o completare rutabilă a selecției care nu produce preț
 * negativ după subvenție. Folosim maximul financiar posibil pe fiecare grup;
 * dacă nici el nu ajunge la zero, nicio combinație permisă nu poate ajunge.
 */
export function selectionHasFinanciallyValidCompletion(
  groups: readonly OfferOptionGroupDto[],
  variants: readonly OfferFulfillmentVariantDto[],
  selection: OptionSelection,
  customerBasePrice: MoneyMinor,
): boolean {
  const candidates: OfferFulfillmentVariantDto[] = variants.length > 0
    ? [...variants]
    : [{
        rules: groups.map((group) => ({
          groupId: group.id,
          minSelect: group.minSelect,
          maxSelect: group.maxSelect,
          required: group.required,
        })),
        availableOptionIds: groups.flatMap((group) =>
          group.options.filter((option) => option.available).map((option) => option.id),
        ),
      }];
  return candidates.some((variant) => {
    if (!variantSupportsSelection(variant, selection, false)) return false;
    const available = new Set(variant.availableOptionIds);
    let maximum = customerBasePrice;
    for (const rule of variant.rules) {
      const group = groups.find((entry) => entry.id === rule.groupId);
      if (group === undefined) return false;
      const selected = selectedIdsFor(selection, rule.groupId);
      const selectedOptions = selected.map((id) => group.options.find((option) => option.id === id));
      if (selectedOptions.some((option) => option === undefined || !option.available || !available.has(option.id))) {
        return false;
      }
      maximum += selectedOptions.reduce((sum, option) => sum + option!.priceDelta, 0);
      const minimum = rule.required ? Math.max(1, rule.minSelect) : rule.minSelect;
      const remainingSlots = rule.maxSelect - selected.length;
      const remaining = group.options
        .filter((option) => option.available && available.has(option.id) && !selected.includes(option.id))
        .sort((left, right) => right.priceDelta - left.priceDelta);
      const requiredMore = Math.max(0, minimum - selected.length);
      if (remainingSlots < requiredMore || remaining.length < requiredMore) return false;
      const chosen = remaining.slice(0, requiredMore);
      const optional = remaining.slice(requiredMore, Math.max(requiredMore, remainingSlots));
      maximum += chosen.reduce((sum, option) => sum + option.priceDelta, 0);
      maximum += optional.filter((option) => option.priceDelta > 0).reduce((sum, option) => sum + option.priceDelta, 0);
    }
    return maximum >= 0;
  });
}

/**
 * Grupurile obligatorii pe care clientul NU are cum sa le satisfaca: grupul cere
 * cel putin `minSelect` alegeri, dar atatea optiuni disponibile nu exista.
 *
 * Core-ul pastreaza deliberat in raspuns un grup ramas fara optiuni (ADR-024:
 * ascunderea lui ar trimite la bucatarie o alegere pe care nimeni nu a facut-o).
 * Consecinta pe ecran trebuie sa fie spusa, nu doar dedusa: fara asta, cardul
 * afiseaza „Alege o opțiune la «Blat»" langa un grup gol si clientul apasa la
 * nesfarsit un buton care nu se poate debloca.
 *
 * Lista goala = nu exista niciun asemenea grup. Blocajele obisnuite, cele pe
 * care clientul le poate rezolva alegand, sunt in `selectionBlockers`.
 */
export function unsatisfiableGroups(groups: OfferOptionGroupDto[]): string[] {
  const reasons: string[] = [];
  for (const group of groups) {
    if (group.minSelect <= 0) continue;
    const availableCount = group.options.filter((option) => option.available).length;
    if (availableCount >= group.minSelect) continue;
    reasons.push(
      availableCount === 0
        ? `La «${group.name}» nu este disponibilă nicio opțiune.`
        : `La «${group.name}» trebuie alese ${group.minSelect} opțiuni, dar sunt disponibile doar ${availableCount}.`,
    );
  }
  return reasons;
}

/** Diferenta totala de pret pe UNITATE, adusa de optiunile alese. */
export function selectionPriceDelta(groups: OfferOptionGroupDto[], selection: OptionSelection): MoneyMinor {
  let delta = 0;
  for (const group of groups) {
    for (const optionId of selectedIdsFor(selection, group.id)) {
      const option = group.options.find((item) => item.id === optionId);
      if (option !== undefined) delta += option.priceDelta;
    }
  }
  return delta;
}

export interface ResolvedOption {
  groupId: string;
  groupName: string;
  optionId: string;
  optionName: string;
  priceDelta: MoneyMinor;
}

/** Optiunile alese, in ordinea grupurilor din meniu — aceeasi ordine pe bon. */
export function resolveSelection(
  groups: OfferOptionGroupDto[],
  selection: OptionSelection,
): ResolvedOption[] {
  const resolved: ResolvedOption[] = [];
  for (const group of groups) {
    for (const option of group.options) {
      if (!selectedIdsFor(selection, group.id).includes(option.id)) continue;
      resolved.push({
        groupId: group.id,
        groupName: group.name,
        optionId: option.id,
        optionName: option.name,
        priceDelta: option.priceDelta,
      });
    }
  }
  return resolved;
}

/**
 * Identitatea liniei de cos. Aceeasi oferta cu optiuni diferite e ALTA linie:
 * unificarea lor ar face imposibil de spus care bucata are extra mozzarella.
 *
 * Cheia e locala browserului si nu pleaca nicaieri: la core se trimit `offerId`
 * plus identitatile optiunilor, iar core-ul isi calculeaza propria referinta de
 * linie pentru POS. Doua formate diferite, doua responsabilitati diferite.
 */
export function cartLineIdFor(offerId: string, options: ResolvedOption[]): string {
  if (options.length === 0) return offerId;
  const parts = options.map((option) => `${option.groupId}:${option.optionId}`).sort();
  return `${offerId}#${parts.join(",")}`;
}
